import { useMemo } from "react"

import type {
  V1FetchRatingTripsResponse,
  V1GetRideSummaryBody,
  V1GetRideSummaryResponse,
  V1RequestRideStatusBody,
  V1RequestRideStatusResponse,
  V1RequestRideStatusWithDriverResponse,
  V2CustomerGetCurrentUserStatusBody,
  V2CustomerGetCurrentUserStatusResponse,
} from "@be-miniapp/api"
import type {
  DriverAcceptedData,
  FullEngagementInfo,
  SSERideStatus,
  TripState,
} from "@be-miniapp/types"
import { useRideStateEffects } from "@be-miniapp/ui/hooks/use-ride-state-effects"
import { useRideStatus } from "@be-miniapp/ui/hooks/use-ride-status"

import { getSessionId } from "./ride-session"

export interface RideSessionControllerDeps {
  v1RequestRideStatus: (
    body: V1RequestRideStatusBody,
    options?: { formUrlEncoded?: boolean }
  ) => Promise<
    V1RequestRideStatusResponse | V1RequestRideStatusWithDriverResponse
  >
  v2CustomerGetCurrentUserStatus: (
    body: V2CustomerGetCurrentUserStatusBody
  ) => Promise<V2CustomerGetCurrentUserStatusResponse>
  v1FetchRatingTrips: () => Promise<V1FetchRatingTripsResponse>
  v1GetRideSummary: (
    body: V1GetRideSummaryBody
  ) => Promise<V1GetRideSummaryResponse>
  BeAutoAssigningDriver: {
    ASSIGNING_DRIVER: number
    RIDE_ACCEPTED: number
    NO_DRIVERS_AVAILABLE: number
  }
  BEAutoRideStatus: {
    rideCancelledByDriver: number
    started: number
  }
  SSERideStatusFlag: {
    CANCELLED_BY_DRIVER: number
    FINISHED: number
  }
  homeSessionsV2QueryKey: { all: unknown }
  handleVibrate: () => void
}

export interface RideSessionControllerUI {
  navigate: (to: string, options?: Record<string, unknown>) => void
  modal: {
    show: (params: Record<string, unknown>) => void
    close: () => void
  }
  t: (key: string) => string
}

export function useRideSessionController(params: {
  sessionIdFromLocation: string | undefined
  sessionIdFromState: string | number | undefined
  currentState: TripState | undefined
  engagement: (DriverAcceptedData & FullEngagementInfo) | undefined
  setCurrentState: (state: TripState | undefined) => void
  setEngagement: (
    sessionData: DriverAcceptedData | FullEngagementInfo,
    reset?: boolean
  ) => void
  setAllowFetchRideSession: (allow: boolean) => void
  resetRideState: () => void

  sseData: SSERideStatus | null

  deps: RideSessionControllerDeps

  ui: RideSessionControllerUI
}) {
  const {
    sessionIdFromLocation,
    sessionIdFromState,
    currentState,
    engagement,
    setCurrentState,
    setEngagement,
    setAllowFetchRideSession,
    resetRideState,
    sseData,
    deps,
    ui,
  } = params

  const sessionId = useMemo(
    () => getSessionId(sessionIdFromLocation, sessionIdFromState),
    [sessionIdFromLocation, sessionIdFromState]
  )

  const currentStateString = currentState || "finding-driver"

  const {
    rideStatus,
    rideSession,
    ratingTrips,
    refetchRideStatus,
    getRideSummary,
  } = useRideStatus({
    sessionId,
    currentState: currentStateString,
    engagement,
    v1RequestRideStatus: deps.v1RequestRideStatus,
    v2CustomerGetCurrentUserStatus: deps.v2CustomerGetCurrentUserStatus,
    v1FetchRatingTrips: deps.v1FetchRatingTrips,
    v1GetRideSummary: deps.v1GetRideSummary,
    BeAutoAssigningDriver: deps.BeAutoAssigningDriver,
  })

  useRideStateEffects({
    rideStatus,
    rideSession,
    ratingTrips,
    engagement,
    currentState: currentStateString,
    sseData: sseData?.data ?? null,
    setCurrentState,
    setEngagement,
    setAllowFetchRideSession,
    resetRideState,
    getRideSummary,
    refetchRideStatus,
    navigate: ui.navigate,
    modal: ui.modal,
    t: ui.t,
    handleVibrate: deps.handleVibrate,
    homeSessionsV2QueryKey: deps.homeSessionsV2QueryKey,
    BeAutoAssigningDriver: deps.BeAutoAssigningDriver,
    BEAutoRideStatus: deps.BEAutoRideStatus,
    SSERideStatusFlag: deps.SSERideStatusFlag,
  })

  return {
    sessionId,
    currentStateString,
    rideStatus,
    rideSession,
    ratingTrips,
    refetchRideStatus,
    getRideSummary,
  }
}
